import React, { Suspense } from 'react';
import loadable from '@loadable/component';
import { CUSTOM_SECTIONS, VALID_SECTION_CODES } from '../../../../../../constants/sectionConstants';
import { getUrlParamsMapV2, isUserLoggedIn } from '../../../../../../../common/utils/browserUtils';
import RecentlyViewSection from './RecentlyViewSection';
import OfferSection from './OffersSection';
import LoginSection from './LoginSection';
import ContentCard from './ContentCard';
import DidYouKnow from './DidYouKnow';
import { checkRVSSection } from '../../../../../../../common/src/utils/utils';
import analytics from '../../../../../../../../analytics';
import AddOnSection from './AddOnSection';

import './styles.scss';

/**
 * Whenever you have an external library which is not ssr compatible
 * i.e it has window and document used inside it without ssr check please use
 * react loadable.
 */
const AdtechCardWrapper = loadable(
  () => import('../../../../../../../common/component/AdtechCardWrapper/AdtechCardWrapper')
);

const ContactManagementWrapper = loadable(
  () => import(/* webpackChunkName: 'ContactMAnagement' */ '../../../../ContactManagementWrapper')
);

const PDOSectionWrapper = loadable(() => import('./PDOSectionWrapper'));

const MmtBlackWidget = loadable(
  () => import('../../../../../../../common/component/MmtBlackWidget')
);

const ListingSection = ({ section, rowNumber, isSectionsLoading, omnitureUtils }) => {
  switch (section.sectionCode) {
    case VALID_SECTION_CODES.PERSUATION:
      return (
        <div className="lv3SectionWrapper">
          <OfferSection sectionData={section} />
        </div>
      );
    case CUSTOM_SECTIONS.ADDS:
      return (
        <div className="lv3SectionWrapper">
          <AdtechCardWrapper section={section} />
        </div>
      );
    case VALID_SECTION_CODES.RECENTLY_VIEWED:
      return (
        <div className="lv3SectionWrapper">
          {!checkRVSSection(getUrlParamsMapV2()) && <RecentlyViewSection sectionData={section} />}
        </div>
      );
    case VALID_SECTION_CODES.PDO:
      return (
        <div className="lv3SectionWrapper">
          {checkRVSSection(getUrlParamsMapV2()) && (
            <Suspense fallback={null}>
              <PDOSectionWrapper
                sectionCode={section?.sectionCode}
                cards={section?.cards}
                header={section?.header}
                id={section?.id}
                rowNumber={rowNumber}
                sectionOrder={section?.order}
                isSectionsLoading={isSectionsLoading}
              />
            </Suspense>
          )}
        </div>
      );
    case VALID_SECTION_CODES.LOGIN:
      return !isUserLoggedIn() ? (
        <div className="lv3SectionWrapper">
          <LoginSection sectionData={section} />
        </div>
      ) : null;
    case VALID_SECTION_CODES.ADDON:
      return (
        <div className="lv3SectionWrapper">
          <AddOnSection sectionData={section} />
        </div>
      );
    case VALID_SECTION_CODES.INQ:
      return (
        <div className="lv3SectionWrapper">
          <ContactManagementWrapper sectionData={section} inlineForm />
        </div>
      );
    case CUSTOM_SECTIONS.CONTENT_CARD:
      return (
        <div className="lv3SectionWrapper">
          <ContentCard sectionData={section} />
        </div>
      );
    case VALID_SECTION_CODES.DYK:
      return (
        <div className="lv3SectionWrapper">
          <DidYouKnow sectionData={section} />
        </div>
      );
    case VALID_SECTION_CODES.BLACK: {
      // eslint-disable-next-line no-undef
      if (!MMT_HLD.mmtBlack) {
        return null;
      }
      const { mmtBlackPdtData } = section?.cards?.[0]?.mmtBlackDetail || {};
      const eVar108Value = `Holidays|listing:v3|MMT${
        mmtBlackPdtData?.mmtBlackGCAttached ? '|GC_1' : '|GC_0'
      }`;
      const trackingData = {
        effectivePriceBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.effectivePriceBucket,
        gcBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.gcBucket,
        myCashBucket: mmtBlackPdtData?.mmtBlackBucketDetail?.myCashBucket,
      };

      return (
        <div
          className={`listing-mmt-black-container ${
            mmtBlackPdtData?.isMmtBlack ? '' : 'listing-mmt-black-normal-user-container'
          }`}
        >
          <MmtBlackWidget
            cards={section?.cards}
            wrapperClasses="listing-page-mmt-black-wrapper"
            omnitureUtils={{
              trackClickEvent: eventName => {
                omnitureUtils.trackClickEvent({
                  prop1: section?.order,
                  prop54: eventName?.clickEvent,
                  eVar108: eVar108Value,
                  eVar76: `${trackingData.gcBucket} | ${trackingData.myCashBucket} | ${trackingData.effectivePriceBucket}`,
                });
              },
              trackLoadEvent: eventName =>
                omnitureUtils.trackLoadEvent({
                  prop1: section?.order,
                  prop54: eventName?.clickEvent,
                  eVar108: eVar108Value,
                  eVar76: `${trackingData.gcBucket} | ${trackingData.myCashBucket} | ${trackingData.effectivePriceBucket}`,
                }),
            }}
            fireAnalytics={pdtObj => {
              analytics.captureEvent({
                event_type: pdtObj.event_type,
                event_name: pdtObj.event_name,
                event_value: `${pdtObj.event_value} | ${section?.sectionCode}`,
              });
            }}
            modalClass={mmtBlackPdtData?.isMmtBlack ? '' : 'black-modal-normal-user'}
          />
        </div>
      );
    }

    default:
      return null;
  }
};

export default ListingSection;
